import {
  AmountDueInfo,
  CreditCardInfo,
  CustomerInfo,
  CustomerType,
  ExperimentalPricingCurve,
  PackageId,
  SelectedUpdate,
} from 'media/js/pricing/api/types';

export interface PlanBuilderStep {
  additionalComponentAttrs?: {[attr: string]: any};
  componentName: string;
  stepId: PlanBuilderStepId;
  stepName: string;
}

export enum PlanBuilderStepId {
  SLIDER = 0,
  PACKAGES_AUTHED = 1,
  OVERAGES_AUTHED = 10,
  PAYMENT_AUTHED = 2,
  REVIEW_AUTHED = 3,
  PACKAGES_UNAUTHED = 20,
  // Add more steps here
}

export interface PlanRecommendation {
  cap: number;
  events_usage: number;
}

const PLAN_BUILDER_STEP_SLIDER: PlanBuilderStep = {
  componentName: `plan-builder-step-slider-25k-2023`, // Use full component names here
  stepId: PlanBuilderStepId.SLIDER,
  stepName: `Events`,
};

// Below are placeholders

const PLAN_BUILDER_STEP_PACKAGES_AUTHED: PlanBuilderStep = {
  componentName: `feature-package-options-2023`,
  stepId: PlanBuilderStepId.PACKAGES_AUTHED,
  stepName: `Add-Ons`,
};

const PLAN_BUILDER_STEP_PACKAGES_UNAUTHED: PlanBuilderStep = {
  componentName: `feature-package-options-2023`,
  stepId: PlanBuilderStepId.PACKAGES_UNAUTHED,
  stepName: `Add-Ons`,
  additionalComponentAttrs: {unauthed: true},
};

const PLAN_BUILDER_STEP_REVIEW_AUTHED: PlanBuilderStep = {
  componentName: `review-plan-2023`,
  stepId: PlanBuilderStepId.REVIEW_AUTHED,
  stepName: `Review`,
};

const PLAN_BUILDER_STEP_PAYMENT_AUTHED: PlanBuilderStep = {
  componentName: `payment-info-2023`,
  stepId: PlanBuilderStepId.PAYMENT_AUTHED,
  stepName: `Payment Info`,
};

export const DEFAULT_PLAN_BUILDER_STEPS: PlanBuilderStep[] = [
  PLAN_BUILDER_STEP_SLIDER,
  PLAN_BUILDER_STEP_PACKAGES_AUTHED,
  PLAN_BUILDER_STEP_PAYMENT_AUTHED,
  PLAN_BUILDER_STEP_REVIEW_AUTHED,
];

export const UNAUTHED_PLAN_BUILDER_STEPS: PlanBuilderStep[] = [
  PLAN_BUILDER_STEP_SLIDER,
  PLAN_BUILDER_STEP_PACKAGES_UNAUTHED,
  // These steps are inaccessible, but are included so that they are visible in the breadcrumb trail
  PLAN_BUILDER_STEP_PAYMENT_AUTHED,
  PLAN_BUILDER_STEP_REVIEW_AUTHED,
];

export const PLAN_BUILDER_STEPS_BY_CUSTOMER_TYPE: {[customerType: number]: PlanBuilderStep[]} = {
  [CustomerType.NONE]: UNAUTHED_PLAN_BUILDER_STEPS,
  [CustomerType.FREE_LEGACY]: DEFAULT_PLAN_BUILDER_STEPS,
  [CustomerType.FREE_MTU]: DEFAULT_PLAN_BUILDER_STEPS,
  [CustomerType.FREE_MTU_100K]: DEFAULT_PLAN_BUILDER_STEPS,
  [CustomerType.SELFSERVE_LEGACY]: DEFAULT_PLAN_BUILDER_STEPS,
  [CustomerType.SELFSERVE_MTU]: DEFAULT_PLAN_BUILDER_STEPS,
  [CustomerType.CONTRACT_LEGACY]: DEFAULT_PLAN_BUILDER_STEPS,
  [CustomerType.CONTRACT_MTU]: DEFAULT_PLAN_BUILDER_STEPS,
  [CustomerType.GENERIC_LEGACY]: DEFAULT_PLAN_BUILDER_STEPS,
  [CustomerType.FREE_PRICING_2023]: DEFAULT_PLAN_BUILDER_STEPS,
  [CustomerType.PREMIUM_PRICING_2023]: DEFAULT_PLAN_BUILDER_STEPS,
};

export interface PlanBuilderState {
  allowPaymentViaCreditCard: boolean;
  customerInfo: CustomerInfo;
  pricingCurves: ExperimentalPricingCurve[];
  selectedUpdate: SelectedUpdate;
  ready: boolean;
  currentStepIdx: number;
  clientSecret?: string; // Client secret for Stripe elements
  showThankYou: boolean;
  amountDueInfo?: AmountDueInfo;
  isBreakdownExpanded: boolean;
  isAmountDueInfoLoading: boolean;
  dontUpdateAmountDue: boolean;
  billingCard?: CreditCardInfo;
  showCurrentPlanPane: boolean;
  planRecommendation: PlanRecommendation;
}

export const MAX_CARD_PAYMENT_THRESHOLD = 76000;

export const PACKAGE_ID_TO_LINK = {
  [PackageId.DATA_WAREHOUSE_EXPORT]: `https://mixpanel.com/data-pipeline/`,
  [PackageId.GROUP_ANALYTICS]: `https://mixpanel.com/group-analytics/`,
};

export const DEFAULT_CAP = 1000000;
